/**
 * UI Feature-Toggle registry — single source of truth for every toggleable
 * control in the webapp. See FEATURE_TOGGLE_PLAN.md for the full inventory.
 *
 * Each entry: { key, label, area, defaultOn }
 *  - key      stable namespaced string ("<area>.<control>")
 *  - label    human-readable label shown in the Settings "Visible features" tab
 *  - area     grouping bucket for the Settings tab
 *  - defaultOn hardcoded default (everything ON except comingSoon controls)
 *
 * Resolution (see FeatureToggleContext): user setting -> domain setting ->
 * DEFAULTS. A key resolves ON unless a setting explicitly sets isActive:false.
 * NEVER_TOGGLE keys always resolve ON regardless of config.
 *
 * TODO(file-tabs): file preview tabs (file.tab.*) and the Files page controls
 * are intentionally NOT registered here yet — the files modules are owned by
 * another workstream. Add file.tab.view/.summary/.metadata/.output/.info once
 * that code stabilises.
 */

export const AREAS = {
  SIDEBAR: "Sidebar",
  CONTACTS_APPBAR: "Contacts app bar",
  PROPERTIES_APPBAR: "Properties app bar",
  MATCH_APPBAR: "Match app bar",
  CAMPAIGNS_APPBAR: "Campaigns app bar",
  CAMPAIGN_DETAILS: "Campaign details",
  AI_APPBAR: "AI app bar",
  JOURNAL: "Journal",
  REMINDERS: "Reminders",
  COMMENTS: "Comments",
  CONTACT_DETAILS: "Contact details",
  PROPERTY_DETAILS: "Property details",
};

// Ordered list of areas for stable rendering in the Settings tab.
export const AREA_ORDER = [
  AREAS.SIDEBAR,
  AREAS.CONTACTS_APPBAR,
  AREAS.PROPERTIES_APPBAR,
  AREAS.MATCH_APPBAR,
  AREAS.CAMPAIGNS_APPBAR,
  AREAS.CAMPAIGN_DETAILS,
  AREAS.AI_APPBAR,
  AREAS.JOURNAL,
  AREAS.REMINDERS,
  AREAS.COMMENTS,
  AREAS.CONTACT_DETAILS,
  AREAS.PROPERTY_DETAILS,
];

export const UI_TOGGLES = [
  /* ── A/B. Sidebar — main + user menu ── */
  { key: "nav.myMagic", label: "My Magic", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.contacts", label: "Contacts", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.properties", label: "Properties", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.match", label: "Match", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.campaigns", label: "Campaigns", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.deals", label: "Deals", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.loans", label: "Loan Inbox", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.feedback", label: "Feedback", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.files", label: "Files", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.todo", label: "ToDo", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.journal", label: "Journal", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "nav.aichat", label: "AI Chat", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "user.profile", label: "Profile", area: AREAS.SIDEBAR, defaultOn: true },
  { key: "user.company", label: "Company", area: AREAS.SIDEBAR, defaultOn: true },
  // user.settings is intentionally NOT toggleable (see NEVER_TOGGLE) — it must
  // always be reachable so the user can re-enable anything they turned off.

  /* ── D. Contacts app bar (panel toggles + global actions) ── */
  { key: "panel.addressbook.contacts", label: "Contacts panel", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "panel.addressbook.details", label: "Details panel", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "panel.addressbook.comments", label: "Comments panel", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "panel.addressbook.chat", label: "Chat panel", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "panel.addressbook.journal", label: "Journal panel", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "panel.addressbook.reminders", label: "Reminders panel", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "panel.addressbook.files", label: "Files panel", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "panel.addressbook.properties", label: "Linked Properties panel", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "appbar.addReminder", label: "+ Reminder button", area: AREAS.CONTACTS_APPBAR, defaultOn: true },
  { key: "appbar.share", label: "Share button (global)", area: AREAS.CONTACTS_APPBAR, defaultOn: true },

  /* ── E. Properties app bar ── */
  { key: "panel.properties.contacts", label: "Contacts panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.map", label: "Map panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.details", label: "Details panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.comments", label: "Comments panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.chat", label: "Chat panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.journal", label: "Journal panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.reminders", label: "Reminders panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.files", label: "Files panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.images", label: "Images panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },
  { key: "panel.properties.verify", label: "Verify panel", area: AREAS.PROPERTIES_APPBAR, defaultOn: true },

  /* ── F. Match app bar ── */
  { key: "panel.match.contacts", label: "Contacts panel", area: AREAS.MATCH_APPBAR, defaultOn: true },
  { key: "panel.match.details", label: "Details panel", area: AREAS.MATCH_APPBAR, defaultOn: true },
  { key: "panel.match.map", label: "Map panel", area: AREAS.MATCH_APPBAR, defaultOn: true },
  { key: "panel.match.chat", label: "Chat panel", area: AREAS.MATCH_APPBAR, defaultOn: true },
  { key: "panel.match.files", label: "Files panel", area: AREAS.MATCH_APPBAR, defaultOn: true },

  /* ── F2. Campaigns app bar ── */
  { key: "panel.campaigns.list", label: "Campaign list panel", area: AREAS.CAMPAIGNS_APPBAR, defaultOn: true },
  { key: "panel.campaigns.details", label: "Campaign details panel", area: AREAS.CAMPAIGNS_APPBAR, defaultOn: true },
  { key: "panel.campaigns.contacts", label: "Campaign contacts panel", area: AREAS.CAMPAIGNS_APPBAR, defaultOn: true },
  { key: "panel.campaigns.analytics", label: "Campaign analytics panel", area: AREAS.CAMPAIGNS_APPBAR, defaultOn: true },

  /* ── F3. Campaign details panel — cards and advanced controls.
     Turn these OFF for a simple, text-driven campaign editor: name, a plain
     "describe your audience" chat, subject, body, Send. ── */
  { key: "campaign.action.dryRun", label: "Dry run button", area: AREAS.CAMPAIGN_DETAILS, defaultOn: true },
  { key: "campaign.section.identity", label: "Campaign Identity card (name + types)", area: AREAS.CAMPAIGN_DETAILS, defaultOn: true },
  { key: "campaign.section.contactTypes", label: "Contact Types Filter card", area: AREAS.CAMPAIGN_DETAILS, defaultOn: true },
  { key: "campaign.section.emailSetup", label: "Email: From / Reply-To / sender addresses", area: AREAS.CAMPAIGN_DETAILS, defaultOn: true },
  { key: "campaign.section.emailTools", label: "Email: HTML upload, preview, test send, attachments", area: AREAS.CAMPAIGN_DETAILS, defaultOn: true },
  { key: "campaign.section.brochure", label: "Brochure Link card", area: AREAS.CAMPAIGN_DETAILS, defaultOn: true },
  { key: "campaign.section.advancedFilter", label: "Advanced Filter card (off = audience chat inline)", area: AREAS.CAMPAIGN_DETAILS, defaultOn: true },
  { key: "campaign.section.reminders", label: "Reminders card", area: AREAS.CAMPAIGN_DETAILS, defaultOn: true },

  /* ── G. My Magic (AI) app bar mode toggles ── */
  { key: "panel.ai.writingMode", label: "Writing Mode", area: AREAS.AI_APPBAR, defaultOn: true },
  { key: "panel.ai.textMode", label: "Text Mode", area: AREAS.AI_APPBAR, defaultOn: true },
  { key: "panel.ai.enrichmentMode", label: "Enrichment Mode", area: AREAS.AI_APPBAR, defaultOn: true },

  /* ── H. Journal panel toolbar + card actions ── */
  { key: "journal.addReminder", label: "Add reminder", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.addText", label: "Add text", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.photo", label: "Photo", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.audio", label: "Record audio", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.speechToText", label: "Speech to text", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.textToSpeech", label: "Text to speech", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.addLink", label: "Add link", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.filter", label: "Filter entries", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.card.readAloud", label: "Card: read aloud", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.card.sms", label: "Card: send via SMS", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.card.edit", label: "Card: edit", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.card.delete", label: "Card: delete", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.reminder.call", label: "Reminder card: call", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.reminder.text", label: "Reminder card: text", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.reminder.email", label: "Reminder card: email", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.reminder.markDone", label: "Reminder card: mark done", area: AREAS.JOURNAL, defaultOn: true },
  { key: "journal.reminder.delete", label: "Reminder card: delete", area: AREAS.JOURNAL, defaultOn: true },

  /* ── I. Reminders panel ── */
  { key: "reminders.createGlobal", label: "Create global reminder", area: AREAS.REMINDERS, defaultOn: true },
  { key: "reminders.card.call", label: "Card: call", area: AREAS.REMINDERS, defaultOn: true },
  { key: "reminders.card.text", label: "Card: text", area: AREAS.REMINDERS, defaultOn: true },
  { key: "reminders.card.email", label: "Card: email", area: AREAS.REMINDERS, defaultOn: true },
  { key: "reminders.card.markDone", label: "Card: mark done", area: AREAS.REMINDERS, defaultOn: true },
  { key: "reminders.card.delete", label: "Card: delete", area: AREAS.REMINDERS, defaultOn: true },
  { key: "reminders.card.editDate", label: "Card: edit date", area: AREAS.REMINDERS, defaultOn: true },
  { key: "reminders.card.editDetails", label: "Card: edit details", area: AREAS.REMINDERS, defaultOn: true },

  /* ── J. Comments panel ── */
  { key: "comments.card.edit", label: "Card: edit", area: AREAS.COMMENTS, defaultOn: true },
  { key: "comments.card.delete", label: "Card: delete", area: AREAS.COMMENTS, defaultOn: true },
  { key: "comments.send", label: "Send comment", area: AREAS.COMMENTS, defaultOn: true },

  /* ── K. Contact detail panel — card sections ── */
  { key: "contact.section.person", label: "Person", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.phones", label: "Phones", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.emails", label: "Emails", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.classification", label: "Classification", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.company", label: "Company", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.organization", label: "Organization", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.details", label: "Details", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.interests", label: "Contact Interests", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.social", label: "Social Media", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.addresses", label: "Addresses", area: AREAS.CONTACT_DETAILS, defaultOn: true },
  { key: "contact.section.notes", label: "Notes", area: AREAS.CONTACT_DETAILS, defaultOn: true },

  /* ── L. Property detail panel — card sections ── */
  { key: "property.section.property", label: "Property", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.owner", label: "Owner", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.statusCategory", label: "Status & Category", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.notes", label: "Notes", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.gis", label: "GIS", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.brochures", label: "Brochures & Public Files", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.info", label: "Property Info", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.amenities", label: "Amenities List", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.marketValue", label: "Current Market Value", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.acquisitionHistory", label: "Acquisition History", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.loans", label: "Loans", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
  { key: "property.section.rentalHistory", label: "Rental History", area: AREAS.PROPERTY_DETAILS, defaultOn: true },
];

/**
 * Controls that must ALWAYS render, regardless of any config. A misconfig must
 * never be able to lock a user out of logging out, switching domains, saving a
 * record, or creating a new one.
 */
export const NEVER_TOGGLE = new Set([
  "user.logout",
  "user.settings", // must always be reachable, else you cannot re-enable anything
  "sidebar.domainSelector",
  "contact.save",
  "property.save",
  "record.save",
  "contact.create",
  "property.create",
]);

// Derived { key: defaultOn } map — the hardcoded default layer.
export const DEFAULTS = UI_TOGGLES.reduce((acc, t) => {
  acc[t.key] = t.defaultOn !== false;
  return acc;
}, {});
